import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { IDetailToInvoice } from '../../types';
import dayjs from '@jobcost-monorepo/dayjs-config';
import {
  cn,
  currencyFormatter,
  InvoicingProviderName,
} from '@jobcost-monorepo/utils';
import IconSVG from '@jobcost-monorepo/ui/atoms/Icons';
import { LabelForm } from '@jobcost-monorepo/ui';

/** Primo valore valorizzato: una stringa vuota non conta come identificativo. */
const firstFilled = (
  ...values: (string | null | undefined)[]
): string | undefined => values.find((value) => !!value?.trim()) ?? undefined;

interface Props {
  detailToInvoice: IDetailToInvoice | undefined;
  startDate: string;
  endDate: string;
  provider?: InvoicingProviderName;
}
const InvoiceFormBody = ({
  detailToInvoice,
  startDate,
  endDate,
  provider,
}: Props): ReactElement => {
  const { t } = useTranslation(['timesheets']);
  const isPrevixio = provider === 'previxio';
  const customer = detailToInvoice?.customer;
  // Con Previxio il cliente si riconosce da ID controparte, P.IVA o CF; con Gestify dall'ID Gestify
  const customerReference = isPrevixio
    ? firstFilled(
        customer?.previxioCounterpartyId,
        customer?.vat,
        customer?.fiscalCode,
      )
    : customer?.externalId;
  const customerReferenceTitle = isPrevixio
    ? t('invoice.previxioCounterparty')
    : t('invoice.externalId');
  const customerReferenceMissing = isPrevixio
    ? t('invoice.previxio.errors.COUNTERPARTY_IDENTIFIER_MISSING')
    : t('invoice.errors.noCustomerExternalId');

  return (
    <div className="flex flex-col w-full max-h-[55vh] overflow-y-auto px-4 sm:max-h-[70vh]">
      {!detailToInvoice ? (
        <span className="block text-md font-medium text-gray-700 dark:text-white">
          {t('invoice.noData')}
        </span>
      ) : (
        <>
          <div>
            <LabelForm
              title={t('invoice.descriptionInvoiceForm')}
              label={dayjs.utc(endDate).format('DD-MM-YYYY')}
              className={'mt-2 mb-6'}
            />
          </div>
          <div className={'flex flex-row flex-wrap mb-2'}>
            <div className={'flex flex-1'}>
              <LabelForm
                title={t('invoice.customer')}
                label={detailToInvoice.customer.name}
                className={'mt-2 mb-2'}
              />
            </div>
            <div
              className={cn(
                'flex flex-1 border-2 rounded-2xl justify-between items-center p-2',
                !customerReference ? 'border-red' : '',
              )}
            >
              <LabelForm
                title={customerReferenceTitle}
                label={
                  customerReference
                    ? String(customerReference)
                    : customerReferenceMissing
                }
                className={'mt-2 mb-2'}
              />
              {!customerReference && (
                <IconSVG
                  iconName={'attention'}
                  className="fill-red stroke-red min-w-6"
                />
              )}
            </div>
          </div>
          <hr />
          <div className={'flex flex-row flex-wrap mb-2 items-start'}>
            <LabelForm
              title={t('invoice.emailTimesheet')}
              label={detailToInvoice.customer.emailTimesheet ?? ''}
              className={'mt-2 mb-2 pr-5 w-fit'}
            />
            <LabelForm
              title={t('invoice.emailInvoice')}
              label={detailToInvoice.customer.emailInvoice ?? ''}
              className={'mt-2 mb-2 pr-5 w-fit'}
            />
            <LabelForm
              title={t('invoice.emailOrder')}
              label={detailToInvoice.customer.emailOrder ?? ''}
              className={'mt-2 mb-2 pr-5 w-fit'}
            />
          </div>
          <hr />
          <div className={'flex flex-row flex-wrap mb-2 items-start'}>
            <LabelForm
              title={t('invoice.period')}
              label={`${dayjs.utc(startDate).format('DD-MM-YYYY')} - ${dayjs.utc(endDate).format('DD-MM-YYYY')}`}
              className={'mt-2 mb-2 pr-5 md:w-1/3'}
            />
            <LabelForm
              title={t('invoice.job')}
              label={`${detailToInvoice.job.code} - ${detailToInvoice.job.description}`}
              className={'mt-2 mb-2 pr-5'}
            />
          </div>
          {detailToInvoice.ordersWithHours.map((orderWithHours) => {
            const netValue =
              orderWithHours.totalWorkedHours *
              (orderWithHours.order.hourRevenue ?? 0);
            const vatValue = netValue * 0.22;
            const grossValue = netValue + vatValue;
            const days =
              Math.ceil((orderWithHours.totalWorkedHours / 8) * 1000) / 1000;
            const dailyRevenue = (orderWithHours.order.hourRevenue ?? 0) * 8;
            const activity = detailToInvoice.activities.find(
              (activity) => activity?.orderId === orderWithHours?.order?.id,
            );

            return (
              <div key={orderWithHours.order.id} className={'mb-2'}>
                <hr />
                <div className={'flex flex-row flex-wrap mb-2 items-start'}>
                  <LabelForm
                    title={t('invoice.order')}
                    label={`${orderWithHours.order.code} - ${orderWithHours.order.description} (${dayjs.utc(orderWithHours.order.createdAt).format('DD-MM-YYYY')})`}
                    className={'mt-2 mb-2 pr-5 md:w-1/3'}
                  />
                  {activity && (
                    <div className={'flex flex-row items-center'}>
                      <LabelForm
                        title={t('invoice.activities')}
                        label={`${t('invoice.nDoc')}: ${activity.invoiceNumber} - ${
                          activity.externalInvoiceId
                            ? t('invoice.externalInvoiceId')
                            : t('invoice.externalId')
                        }: (${activity.externalInvoiceId ?? activity.externalId}) [${activity.workedHours}/h]`}
                        className={'mt-2 mb-2 pr-5'}
                      />
                      <IconSVG
                        iconName="payment"
                        className="fill-black stroke-black dark:fill-white dark:stroke-white size-8 ml-4"
                      />
                    </div>
                  )}
                </div>
                <div className={'flex flex-row flex-wrap mb-2 items-start'}>
                  <LabelForm
                    title={t('invoice.hourRevenue')}
                    label={`${currencyFormatter({
                      value: orderWithHours.order.hourRevenue ?? 0,
                      options: {},
                    })}/h
              
              (${currencyFormatter({
                value: dailyRevenue,
                options: {},
              })}/gg)
              `}
                    className={'mt-2 mb-2 pr-5 md:w-1/3'}
                  />
                  <LabelForm
                    title={t('invoice.hoursTotal')}
                    label={`${orderWithHours.totalWorkedHours}h (${days} gg)`}
                    className={'mt-2 mb-2 pr-5'}
                  />
                </div>
                <div className={'flex flex-row flex-wrap mb-2 items-start'}>
                  <LabelForm
                    title={t('invoice.netValue')}
                    label={`${currencyFormatter({
                      value: netValue,
                      options: {},
                    })}`}
                    className={'mt-2 mb-2 pr-5 md:w-1/3'}
                  />
                  <LabelForm
                    title={t('invoice.vatValue')}
                    label={`${currencyFormatter({
                      value: vatValue,
                      options: {},
                    })}`}
                    className={'mt-2 mb-2 pr-5 md:w-1/3'}
                  />
                  <LabelForm
                    title={t('invoice.grossValue')}
                    label={`${currencyFormatter({
                      value: grossValue,
                      options: {},
                    })}`}
                    className={'mt-2 mb-2 pr-5'}
                  />
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default InvoiceFormBody;
